export interface AdditionalCode {
  code: string
  description: string
  type: string
}

export interface HSSuggestion {
  code: string
  description: string
}

export interface HSCodeValidation {
  hs_code: string
  valid: boolean
  official_description: string | null
  duty_rate: string | null
  additional_codes: AdditionalCode[]
  required_documents: string[] | null
  error: string | null
  suggestions?: HSSuggestion[]
  cn_duty_rate?: string | null
  cn_additional_codes?: AdditionalCode[]
}

function extractDutyRateFromMeasures(measures: any[], included: any[]): string | null {
  if (!measures || measures.length === 0) return null

  const dutyExpressions = new Map<string, any>()
  included.forEach((item: any) => {
    if (item.type === 'duty_expression') {
      dutyExpressions.set(item.id, item)
    }
  })

  for (const measure of measures) {
    const measureType = measure.relationships?.measure_type?.data?.id
    if (!measureType) continue

    const isImportDuty = measureType === '103'
    if (!isImportDuty) continue

    const geoArea = measure.relationships?.geographical_area?.data?.id
    const isApplicable = !geoArea || geoArea === 'ERGA' || geoArea === '1011'

    if (isApplicable) {
      const dutyExprId = measure.relationships?.duty_expression?.data?.id
      if (dutyExprId && dutyExpressions.has(dutyExprId)) {
        const dutyExpr = dutyExpressions.get(dutyExprId)
        return dutyExpr.attributes?.base || null
      }
    }
  }

  return null
}

function extractAdditionalCodesForCN(included: any[]): AdditionalCode[] {
  if (!included || !Array.isArray(included)) return []

  const additionalCodes: AdditionalCode[] = []
  const additionalCodeData = included.filter(item => item.type === 'additional_code' || item.type === 'additionalCode')

  for (const code of additionalCodeData) {
    const codeNum = code.id || code.code
    const desc = code.description || ''
    const codeType = code.additional_code_type?.description || code.type || 'Additional Code'

    if (codeNum && desc) {
      additionalCodes.push({
        code: codeNum,
        description: desc,
        type: codeType
      })
    }
  }

  return additionalCodes
}

function extractDutyRateForCN(measures: any[], included: any[]): string | null {
  if (!measures || measures.length === 0) return null

  const dutyExpressions = new Map<string, any>()
  included.forEach((item: any) => {
    if (item.type === 'duty_expression') {
      dutyExpressions.set(item.id, item)
    }
  })

  for (const measure of measures) {
    const measureType = measure.relationships?.measure_type?.data?.id
    if (!measureType) continue

    const isImportDuty = measureType === '103'
    if (!isImportDuty) continue

    const geoArea = measure.relationships?.geographical_area?.data?.id
    const isCN = geoArea === '2500' || geoArea === 'CN'

    if (isCN) {
      const dutyExprId = measure.relationships?.duty_expression?.data?.id
      if (dutyExprId && dutyExpressions.has(dutyExprId)) {
        const dutyExpr = dutyExpressions.get(dutyExprId)
        return dutyExpr.attributes?.base || null
      }
    }
  }

  return null
}

function extractAdditionalCodesFromCNMeasures(measures: any[], included: any[]): AdditionalCode[] {
  if (!measures || !Array.isArray(measures) || !included || !Array.isArray(included)) return []

  const cnAdditionalCodeIds = new Set<string>()

  for (const measure of measures) {
    const geoArea = measure.relationships?.geographical_area?.data?.id
    const isCN = geoArea === '2500' || geoArea === 'CN'

    if (!isCN) continue

    const additionalCodeRefs = measure.relationships?.additional_codes?.data || []
    for (const ref of additionalCodeRefs) {
      if (ref.id) cnAdditionalCodeIds.add(ref.id)
    }
  }

  const additionalCodes: AdditionalCode[] = []
  const additionalCodeData = included.filter(item => item.type === 'additional_code')

  for (const code of additionalCodeData) {
    const codeNum = code.id
    const desc = code.attributes?.description || ''
    const codeType = code.attributes?.additional_code_type || 'Additional Code'

    if (codeNum && desc && cnAdditionalCodeIds.has(codeNum)) {
      additionalCodes.push({
        code: codeNum,
        description: desc,
        type: codeType
      })
    }
  }

  return additionalCodes
}

function extractRequiredDocuments(measures: any[], included: any[]): string[] | null {
  if (!measures || measures.length === 0) return null

  const measureConditions = new Map<string, any>()
  included.forEach((item: any) => {
    if (item.type === 'measure_condition') {
      measureConditions.set(item.id, item)
    }
  })

  const documents = new Set<string>()

  for (const measure of measures) {
    const conditionIds = measure.relationships?.measure_conditions?.data || []
    for (const condData of conditionIds) {
      const cond = measureConditions.get(condData.id)
      if (cond) {
        const docCode = cond.attributes?.document_code
        if (docCode) {
          documents.add(docCode)
        }
      }
    }
  }

  return documents.size > 0 ? Array.from(documents) : null
}

function extractHeadingDescription(included: any[]): string | null {
  const heading = included.find((item: any) => item.type === 'heading')
  return heading?.attributes?.description || null
}

export async function searchHSCodeSuggestions(hsCode: string): Promise<HSSuggestion[]> {
  try {
    const first6Digits = hsCode.substring(0, 6)
    const response = await fetch(
      `https://www.trade-tariff.service.gov.uk/api/v2/search?q=${encodeURIComponent(first6Digits)}`
    )

    if (!response.ok) return []

    const json = await response.json() as any
    const searchResult = json.data

    if (!searchResult) return []

    const searchType = searchResult.type

    if (searchType === 'exact_search') {
      const entry = searchResult.attributes?.entry
      if (entry && entry.endpoint === 'commodities') {
        const codeId = entry.id
        try {
          const commodityResponse = await fetch(
            `https://www.trade-tariff.service.gov.uk/api/v2/commodities/${codeId}`
          )
          if (commodityResponse.ok) {
            const commodityData = await commodityResponse.json() as any
            const description = commodityData.data?.attributes?.description || ''
            const code = commodityData.data?.attributes?.goods_nomenclature_item_id || codeId || ''
            if (code && description) {
              console.log('Found commodity suggestion for', first6Digits, ':', code, description)
              return [{ code, description }]
            }
          }
        } catch (e) {
          console.error('Error fetching commodity details:', e)
        }
      }
      return []
    }

    const isFuzzyMatch = searchType === 'fuzzy_search'
    if (!isFuzzyMatch) return []

    const commodities = searchResult.attributes?.goods_nomenclature_match?.commodities || []

    const suggestions = commodities
      .slice(0, 3)
      .map((item: any) => ({
        code: item.goods_nomenclature_item_id || item.code || '',
        description: item.description || ''
      }))
      .filter((s: HSSuggestion) => s.code && s.description)

    console.log('Found fuzzy match suggestions for', first6Digits, ':', suggestions)
    return suggestions
  } catch (e) {
    console.error('Error fetching suggestions:', e)
    return []
  }
}

// Helper to limit concurrent requests
async function batchedPromises<T>(tasks: (() => Promise<T>)[], concurrency: number): Promise<T[]> {
  const results: T[] = []
  for (let i = 0; i < tasks.length; i += concurrency) {
    const batch = tasks.slice(i, i + concurrency)
    const batchResults = await Promise.all(batch.map(task => task()))
    results.push(...batchResults)
  }
  return results
}

export async function validateHSCodesWithHMRC(hsCodes: string[]): Promise<HSCodeValidation[]> {
  const unique = Array.from(new Set(hsCodes.filter(c => c && c !== 'UNKNOWN')))

  // Limit concurrent requests to 3 at a time to avoid overwhelming the API
  const tasks = unique.map(hs_code => async () => {
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 8000) // 8 second timeout

      const response = await fetch(
        `https://www.trade-tariff.service.gov.uk/api/v2/commodities/${hs_code}`,
        { signal: controller.signal }
      )
      clearTimeout(timeoutId)

      if (!response.ok) {
        const isNotFound = response.status === 404
        // Skip suggestions for invalid codes to save time - just report the error
        return {
          hs_code,
          valid: false,
          official_description: null,
          duty_rate: null,
          additional_codes: [],
          required_documents: null,
          error: isNotFound ? 'Code not found in HMRC database' : `API error: ${response.status}`,
          suggestions: undefined
        }
      }

      const json = await response.json() as any
      let description = json.data?.attributes?.description || null

      const included = json.included || []
      const headingDesc = extractHeadingDescription(included)
      if (headingDesc && description) {
        description = `${headingDesc} (${description})`
      } else if (headingDesc) {
        description = headingDesc
      }

      const measures = included.filter((item: any) => item.type === 'measure')

      const dutyRate = extractDutyRateFromMeasures(measures, included)
      const additionalCodes = extractAdditionalCodesForCN(included)
      const requiredDocuments = extractRequiredDocuments(measures, included)
      const cnDutyRate = extractDutyRateForCN(measures, included)
      const cnAdditionalCodes = extractAdditionalCodesFromCNMeasures(measures, included)

      return {
        hs_code,
        valid: true,
        official_description: description,
        duty_rate: dutyRate,
        additional_codes: additionalCodes,
        required_documents: requiredDocuments,
        error: null,
        cn_duty_rate: cnDutyRate,
        cn_additional_codes: cnAdditionalCodes
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Validation failed'
      return {
        hs_code,
        valid: false,
        official_description: null,
        duty_rate: null,
        additional_codes: [],
        required_documents: null,
        error: errMsg.includes('abort') ? 'Validation timeout' : errMsg
      }
    }
  })

  // Run max 3 concurrent requests at a time
  const results = await batchedPromises(tasks, 3)
  return results
}
